import { ArrowLeft, Check, Pencil } from "lucide-react";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
  CharacterCreationChoiceSummaryDnd5e,
  CharacterCreationEvaluationDnd5e,
  CharacterCreationInventoryItemDnd5e,
  CharacterCreationPreviewProvenanceDnd5e,
} from "@/lib/core/schemas/character-creation";
import type { CharacterSheetDnd5e } from "@/lib/rules/dnd5e/character/character-sheet";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"] as const;

const signed = (value: number): string =>
  value >= 0 ? `+${value}` : `${value}`;

const formatSpeed = (speeds: CharacterSheetDnd5e["speeds"]): string => {
  const entries = Object.entries(speeds ?? {});
  if (entries.length === 0) {
    return "—";
  }
  return entries
    .map(([mode, value]) => `${mode.replace("movement:", "")} ${value} ft`)
    .join(", ");
};

const classLine = (sheet: CharacterSheetDnd5e): string =>
  sheet.classes
    ?.map((entry) => `${entry.className} ${entry.level}`)
    .join(" / ") ||
  sheet.creatureType ||
  "—";

const sourceNodesFor = (
  provenance: readonly CharacterCreationPreviewProvenanceDnd5e[],
  sectionKey: string
): readonly string[] =>
  provenance.find((entry) => entry.sectionKey === sectionKey)?.sourceNodeIds ??
  [];

function SectionHeader({
  title,
  changeLabel,
  sourceNodeIds,
  onChangeNode,
}: {
  readonly title: string;
  readonly changeLabel: string;
  readonly sourceNodeIds: readonly string[];
  readonly onChangeNode: (nodeId: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="font-semibold text-base text-foreground">{title}</h2>
      {sourceNodeIds[0] ? (
        <Button
          aria-label={changeLabel}
          onClick={() => onChangeNode(sourceNodeIds[0])}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

function ReviewSection({
  title,
  changeLabel,
  sourceNodeIds,
  onChangeNode,
  children,
}: {
  readonly title: string;
  readonly changeLabel: string;
  readonly sourceNodeIds: readonly string[];
  readonly onChangeNode: (nodeId: string) => void;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-border border-t px-4 py-4">
      <SectionHeader
        changeLabel={changeLabel}
        onChangeNode={onChangeNode}
        sourceNodeIds={sourceNodeIds}
        title={title}
      />
      {children}
    </section>
  );
}

function ChoiceSummary({
  choices,
  onChangeNode,
}: {
  readonly choices: readonly CharacterCreationChoiceSummaryDnd5e[];
  readonly onChangeNode: (nodeId: string) => void;
}) {
  return (
    <div className="grid gap-2">
      {choices.map((choice) => (
        <div
          className="grid grid-cols-[1fr_auto] gap-3 rounded-md border border-border p-3"
          key={choice.nodeId}
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-sm">{choice.label}</span>
              <Badge className="text-[10px]" variant="secondary">
                {choice.category}
              </Badge>
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              {choice.selectedLabels.join(", ")}
            </p>
          </div>
          <Button
            aria-label={`Change ${choice.label}`}
            onClick={() => onChangeNode(choice.nodeId)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function StartingInventory({
  items,
}: {
  readonly items: readonly CharacterCreationInventoryItemDnd5e[];
}) {
  if (items.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No starting equipment selected.
      </p>
    );
  }
  return (
    <ul className="grid gap-2">
      {items.map((item) => (
        <li
          className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
          key={item.temporaryEntityId}
        >
          <span>{item.label}</span>
          <span className="text-muted-foreground">x{item.quantity}</span>
        </li>
      ))}
    </ul>
  );
}

function Features({ sheet }: { readonly sheet: CharacterSheetDnd5e }) {
  const features = sheet.features ?? [];
  if (features.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No readable features are available yet.
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {features.map((feature) => (
        <li className="space-y-1" key={feature.id}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">{feature.label}</span>
            <Badge className="text-[10px]" variant="secondary">
              {feature.sourceLabel}
            </Badge>
            {feature.level === undefined ? null : (
              <Badge className="text-[10px]" variant="outline">
                Level {feature.level}
              </Badge>
            )}
          </div>
          {feature.description.trim() ? (
            <p className="text-muted-foreground text-sm">
              {feature.description}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function CharacterReviewStep({
  evaluation,
  onBack,
  onChangeNode,
  onConfirm,
  confirmDisabled = false,
  confirmLabel = "Confirm",
}: {
  readonly evaluation: CharacterCreationEvaluationDnd5e;
  readonly onBack: () => void;
  readonly onChangeNode: (nodeId: string) => void;
  readonly onConfirm: () => void | Promise<void>;
  readonly confirmDisabled?: boolean;
  readonly confirmLabel?: string;
}) {
  const preview = evaluation.preview;
  if (!preview) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="font-medium text-sm">Draft preview is not available.</p>
        <Button onClick={onBack} type="button" variant="outline">
          <ArrowLeft className="h-4 w-4" />
          Back to choices
        </Button>
      </div>
    );
  }

  const { sheet } = preview;
  const pendingChoiceCount =
    (sheet.pendingProfileProficiencyChoices?.length ?? 0) +
    (sheet.pendingExpertiseChoices?.length ?? 0) +
    (sheet.pendingMetamagicChoices?.length ?? 0) +
    (sheet.pendingWeaponMasteryChoices?.length ?? 0);
  const defenseCount =
    (sheet.damageResistances?.length ?? 0) +
    (sheet.damageImmunities?.length ?? 0) +
    (sheet.damageVulnerabilities?.length ?? 0) +
    (sheet.conditionImmunities?.length ?? 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="sticky top-0 z-10 border-b bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <Button
            aria-label="Back to choices"
            onClick={onBack}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 text-center">
            <h1 className="truncate font-semibold text-base">
              Review {sheet.name}
            </h1>
            <p className="truncate text-muted-foreground text-xs">
              {preview.planDigest}
            </p>
          </div>
          <Button disabled={confirmDisabled} onClick={onConfirm} type="button">
            {confirmLabel}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)]">
        <div className="mx-auto max-w-3xl">
          <ReviewSection
            changeLabel="Change identity choices"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "identity")}
            title="Identity"
          >
            <div className="space-y-1 text-sm">
              <p className="font-medium text-lg">{sheet.name}</p>
              <p className="text-muted-foreground capitalize">
                {classLine(sheet)}
              </p>
              <p className="text-muted-foreground">
                Level {sheet.totalLevel} · {sheet.size ?? "unknown size"} ·{" "}
                {sheet.creatureType ?? "creature"}
              </p>
            </div>
          </ReviewSection>

          <ReviewSection
            changeLabel="Change vital source choices"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "vitals")}
            title="Vitals"
          >
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
              <div className="rounded-md bg-secondary/50 p-3">
                AC <b>{sheet.ac}</b>
              </div>
              <div className="rounded-md bg-secondary/50 p-3">
                HP{" "}
                <b>
                  {sheet.hp.current}/{sheet.hp.max}
                </b>
              </div>
              <div className="rounded-md bg-secondary/50 p-3">
                Init <b>{signed(sheet.initiativeBonus)}</b>
              </div>
              <div className="rounded-md bg-secondary/50 p-3">
                PB <b>+{sheet.proficiencyBonus}</b>
              </div>
              <div className="rounded-md bg-secondary/50 p-3 sm:col-span-1">
                Speed <b>{formatSpeed(sheet.speeds)}</b>
              </div>
            </div>
          </ReviewSection>

          <ReviewSection
            changeLabel="Change ability scores"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "abilities")}
            title="Abilities"
          >
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {ABILITIES.map((ability) => {
                const entry = sheet.abilities?.[ability];
                if (!entry) {
                  return null;
                }
                return (
                  <div
                    className="flex flex-col items-center rounded-md bg-secondary/50 p-3"
                    key={ability}
                  >
                    <span className="text-muted-foreground text-xs uppercase">
                      {ability}
                    </span>
                    <span className="font-bold text-xl">{entry.score}</span>
                    <span className="text-primary text-xs">
                      {signed(entry.modifier)}
                    </span>
                  </div>
                );
              })}
            </div>
          </ReviewSection>

          <ReviewSection
            changeLabel="Change creation choices"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "features")}
            title="Creation choices"
          >
            <ChoiceSummary
              choices={preview.choiceSummary}
              onChangeNode={onChangeNode}
            />
          </ReviewSection>

          <ReviewSection
            changeLabel="Change starting equipment"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "equipment")}
            title="Starting equipment"
          >
            <StartingInventory items={preview.inventory} />
          </ReviewSection>

          <ReviewSection
            changeLabel="Change feature source choices"
            onChangeNode={onChangeNode}
            sourceNodeIds={sourceNodesFor(preview.provenance, "features")}
            title="Features"
          >
            <Features sheet={sheet} />
          </ReviewSection>

          <Accordion className="border-border border-t px-4" type="multiple">
            <AccordionItem value="proficiencies">
              <AccordionTrigger>Proficiencies and defenses</AccordionTrigger>
              <AccordionContent className="space-y-3 text-sm">
                <div>
                  <b>Saves:</b> {sheet.proficientSaves?.join(", ") || "—"}
                </div>
                <div>
                  <b>Skills:</b> {sheet.proficientSkills?.join(", ") || "—"}
                </div>
                {defenseCount > 0 ? (
                  <div className="space-y-1">
                    {(sheet.damageResistances?.length ?? 0) > 0 ? (
                      <p>Resist: {sheet.damageResistances?.join(", ")}</p>
                    ) : null}
                    {(sheet.damageImmunities?.length ?? 0) > 0 ? (
                      <p>Immune: {sheet.damageImmunities?.join(", ")}</p>
                    ) : null}
                    {(sheet.damageVulnerabilities?.length ?? 0) > 0 ? (
                      <p>
                        Vulnerable: {sheet.damageVulnerabilities?.join(", ")}
                      </p>
                    ) : null}
                    {(sheet.conditionImmunities?.length ?? 0) > 0 ? (
                      <p>
                        Condition immune:{" "}
                        {sheet.conditionImmunities?.join(", ")}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="rolls">
              <AccordionTrigger>Saves, skills, and checks</AccordionTrigger>
              <AccordionContent className="space-y-2 text-sm">
                <p>Detailed roll breakdowns remain available after creation.</p>
                <p>Pending post-creation choices: {pendingChoiceCount}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="px-4 py-5">
            <Button className="w-full" onClick={onConfirm} type="button">
              Confirm draft
              <Check className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
